import { ErrorSidePanel } from '@/components/errors';
import { useForm } from '@deadcow-enterprises/react-forms';
import { logger, templates, useI18n, type SkinPageConfig } from '@nosinovacao/nosid-mfe-common';
import { createFileRoute, useLocation } from '@tanstack/react-router';
import { useState } from 'react';
import z from 'zod';
import { useGetPasswordRequirements, useUpdatePassword } from '../-hooks/password.hooks';
import { type NewPasswordProps } from './-model/props';
import { NewPasswordLight, NewPasswordWooDark, NewPasswordWoo } from './-skins';
import { type SecurityCode } from '@/portal-api.definition';

export const Route = createFileRoute(
  '/(home)/home/(change-password)/_sheet/change-password/new-password/',
)({
  component: RouteComponent,
  errorComponent: ErrorSidePanel,
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { t } = useI18n();
  const { state } = useLocation();
  const oldPassword = (state as unknown as { oldPassword?: string }).oldPassword;
  const securityCode = (state as unknown as { securityCode?: SecurityCode }).securityCode;
  const [logoutAllDevices, setLogoutAllDevices] = useState(false);
  const { data: requirements } = useGetPasswordRequirements();

  const form = useForm<{ password: string }>(
    {
      password: {
        value: '',
        id: 'new-password',
        validator: z.string().min(1, t('Home.NewPassword.Errors.PasswordRequired')),
      },
    },
    { onError: logger.logFormErrors.bind(logger) },
  );

  const { mutate: updatePassword, isPending } = useUpdatePassword({
    onSuccess: () => {
      void navigate({ to: '/home' });
    },
    onError: () => {
      form.addError('password', t('Home.NewPassword.Errors.PasswordInvalid'));
    },
  });

  const handleSubmit = ({ password }: typeof form.value) => {
    updatePassword({
      NewPassword: password,
      ...(oldPassword ? { OldPassword: oldPassword } : {}),
      ...(securityCode ? { SecurityCode: securityCode } : {}),
    });
  };

  const props: NewPasswordProps = {
    form,
    isPending,
    requirements,
    logoutAllDevices,
    onLogoutAllDevicesChange: () => setLogoutAllDevices((prev) => !prev),
    onSubmit: handleSubmit,
  };

  return templates.getSkinSelector(skinConfig(props));
}

function skinConfig(props: NewPasswordProps): SkinPageConfig {
  return {
    skins: [
      {
        selector: 'isWooTvTemplate',
        component: <NewPasswordWooDark {...props} />,
      },
      {
        selector: 'isWooTemplate',
        component: <NewPasswordWoo {...props} />,
      },
    ],
    defaultValue: <NewPasswordLight {...props} />,
  };
}
